import { useEffect, useRef, useState } from "react";
import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import {
  submitContractAction,
  type ContractActionReceipt,
} from "../integrations/adminApi";
import { WITHDRAWAL_NOTICE_VERSION } from "../config/legalVersions";
import { PageActions } from "./PageActions";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();

const copy = {
  en: {
    legal: "Contract declaration",
    cancelTitle: "Cancel your contract",
    withdrawTitle: "Withdraw from your contract",
    cancelIntro:
      "Submit your cancellation directly to SailPlot. No account or login is required.",
    withdrawIntro:
      "Submit your withdrawal declaration electronically. A reason is not required.",
    stepOne: "Your declaration",
    name: "Full name",
    email: "Purchase email",
    reference: "Order, transaction, or subscription reference",
    cancelType: "Type of cancellation",
    ordinary: "Ordinary cancellation",
    extraordinary: "Extraordinary cancellation",
    requestedDate: "Requested contract end",
    earliest: "At the earliest legally possible date",
    reason: "Reason for extraordinary cancellation",
    confirmCancel: "cancel now",
    confirmWithdraw: "Confirm withdrawal",
    cancelConfirmation:
      "I confirm that the information is correct and that I want to submit this cancellation.",
    withdrawConfirmation:
      "I confirm that the information is correct and that I want to submit this withdrawal.",
    confirmationRequired: "Confirm the declaration before submitting it.",
    humanReview:
      "Every declaration is reviewed by a person and requires personal approval. Submitting this form does not automatically contact Paddle, issue a refund, or take a site offline.",
    received: "Declaration received",
    receiptText:
      "SailPlot stored your declaration. Keep this receipt and the confirmation email as evidence. Legal validity and any resulting action are reviewed separately.",
    download: "Download receipt",
    print: "Print receipt",
    referenceLabel: "Reference",
    receivedAt: "Received",
    emailPending: "A confirmation is being sent to the supplied email address.",
    error: "The declaration could not be sent. Please try again.",
    captcha: "Complete the security check before confirming.",
    noticeVersion: "Notice version",
  },
  de: {
    legal: "Vertragserklärung",
    cancelTitle: "Vertrag kündigen",
    withdrawTitle: "Vertrag widerrufen",
    cancelIntro:
      "Reiche deine Kündigung direkt bei SailPlot ein. Ein Konto oder Login ist nicht erforderlich.",
    withdrawIntro:
      "Reiche deinen Widerruf elektronisch ein. Eine Begründung ist nicht erforderlich.",
    stepOne: "Deine Erklärung",
    name: "Vollständiger Name",
    email: "Beim Kauf verwendete E-Mail-Adresse",
    reference: "Bestell-, Transaktions- oder Abonnementreferenz",
    cancelType: "Art der Kündigung",
    ordinary: "Ordentliche Kündigung",
    extraordinary: "Außerordentliche Kündigung",
    requestedDate: "Gewünschtes Vertragsende",
    earliest: "Zum nächstmöglichen rechtlich zulässigen Zeitpunkt",
    reason: "Grund der außerordentlichen Kündigung",
    confirmCancel: "jetzt kündigen",
    confirmWithdraw: "Widerruf bestätigen",
    cancelConfirmation:
      "Ich bestätige, dass die Angaben richtig sind und ich diese Kündigung absenden möchte.",
    withdrawConfirmation:
      "Ich bestätige, dass die Angaben richtig sind und ich diesen Widerruf absenden möchte.",
    confirmationRequired: "Bestätige die Erklärung vor dem Absenden.",
    humanReview:
      "Jede Erklärung benötigt eine persönliche Freigabe. Das Absenden kontaktiert Paddle nicht automatisch, löst keine Erstattung aus und schaltet keine Seite offline.",
    received: "Erklärung eingegangen",
    receiptText:
      "SailPlot hat deine Erklärung gespeichert. Bewahre diese Bestätigung und die E-Mail als Nachweis auf. Wirksamkeit und Folgemaßnahmen werden separat geprüft.",
    download: "Bestätigung herunterladen",
    print: "Bestätigung drucken",
    referenceLabel: "Referenz",
    receivedAt: "Eingegangen",
    emailPending:
      "Eine Bestätigung wird an die angegebene E-Mail-Adresse gesendet.",
    error:
      "Die Erklärung konnte nicht gesendet werden. Bitte versuche es erneut.",
    captcha: "Schließe vor dem Bestätigen die Sicherheitsprüfung ab.",
    noticeVersion: "Informationsstand",
  },
} as const;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-sailplot-turnstile="true"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.sailplotTurnstile = "true";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(), { once: true });
    document.head.append(script);
  });
};

export function createContractActionPage(action: "cancel" | "withdraw") {
  return function ContractActionPage({
    language,
    navigate,
  }: SailPlotExtensionContext) {
    const text = copy[language];
    const turnstile = useRef<HTMLDivElement>(null);
    const widgetId = useRef<string>();
    const idempotencyKey = useRef(crypto.randomUUID());
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [reference, setReference] = useState("");
    const [cancellationType, setCancellationType] = useState<
      "ordinary" | "extraordinary"
    >("ordinary");
    const [requestedDate, setRequestedDate] = useState("");
    const [reason, setReason] = useState("");
    const [confirmation, setConfirmation] = useState(false);
    const [token, setToken] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [receipt, setReceipt] = useState<ContractActionReceipt | null>(null);

    useEffect(() => {
      if (!siteKey || !turnstile.current) return;
      let cancelled = false;
      void loadTurnstile().then(() => {
        if (cancelled || !turnstile.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(turnstile.current, {
          sitekey: siteKey,
          action: "contract_action",
          theme:
            document.documentElement.dataset.theme === "dark"
              ? "dark"
              : "light",
          callback: setToken,
          "expired-callback": () => setToken(""),
          "error-callback": () => setToken(""),
        });
      });
      return () => {
        cancelled = true;
        if (widgetId.current && window.turnstile) {
          window.turnstile.remove(widgetId.current);
          widgetId.current = undefined;
        }
      };
    }, []);

    const submit = async () => {
      if (!confirmation) {
        setError(text.confirmationRequired);
        return;
      }
      if (!token) {
        setError(text.captcha);
        return;
      }
      setSending(true);
      setError("");
      try {
        setReceipt(
          await submitContractAction(
            {
              action,
              ...(action === "cancel" ? { cancellationType } : {}),
              name,
              email,
              contractReference: reference,
              ...(action === "cancel" && requestedDate
                ? { requestedEffectiveAt: requestedDate }
                : {}),
              ...(action === "cancel" && cancellationType === "extraordinary"
                ? { reason }
                : {}),
              language,
              turnstileToken: token,
              confirmation: true,
            },
            idempotencyKey.current,
          ),
        );
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : text.error);
        window.turnstile?.reset(widgetId.current || "");
        setToken("");
      } finally {
        setSending(false);
      }
    };

    const downloadReceipt = () => {
      if (!receipt) return;
      const lines = [
        action === "cancel" ? text.cancelTitle : text.withdrawTitle,
        `${text.referenceLabel}: ${receipt.referenceId}`,
        `${text.receivedAt}: ${receipt.receivedAt}`,
        `${text.name}: ${name}`,
        `${text.email}: ${email}`,
        `${text.reference}: ${reference}`,
        `${text.noticeVersion}: ${WITHDRAWAL_NOTICE_VERSION}`,
      ];
      const url = URL.createObjectURL(
        new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" }),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${receipt.referenceId}.txt`;
      anchor.click();
      URL.revokeObjectURL(url);
    };

    if (receipt) {
      return (
        <article className="platform-page platform-reference-page contract-action-page">
          <section className="contract-receipt">
            <span className="contract-receipt__check" aria-hidden="true">
              ✓
            </span>
            <span className="platform-page-eyebrow">{text.legal}</span>
            <h1>{text.received}</h1>
            <p>{text.receiptText}</p>
            <dl>
              <div>
                <dt>{text.referenceLabel}</dt>
                <dd>{receipt.referenceId}</dd>
              </div>
              <div>
                <dt>{text.receivedAt}</dt>
                <dd>{new Date(receipt.receivedAt).toLocaleString(language)}</dd>
              </div>
            </dl>
            <p className="contract-human-review">{text.humanReview}</p>
            <small>{text.emailPending}</small>
            <div className="contract-action-buttons">
              <button
                className="customize-button customize-button--primary"
                type="button"
                onClick={downloadReceipt}
              >
                {text.download}
              </button>
              <button
                className="customize-button customize-button--secondary"
                type="button"
                onClick={() => window.print()}
              >
                {text.print}
              </button>
            </div>
          </section>
          <PageActions language={language} navigate={navigate} />
        </article>
      );
    }

    return (
      <article className="platform-page platform-reference-page contract-action-page">
        <header>
          <span className="platform-page-eyebrow">{text.legal}</span>
          <h1>{action === "cancel" ? text.cancelTitle : text.withdrawTitle}</h1>
          <p>{action === "cancel" ? text.cancelIntro : text.withdrawIntro}</p>
        </header>
        <p className="contract-human-review">{text.humanReview}</p>
        <form
          className="contract-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <h2>{text.stepOne}</h2>
          <div className="contract-form-grid">
            <label>
              <span>{text.name}</span>
              <input
                required
                maxLength={160}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label>
              <span>{text.email}</span>
              <input
                required
                type="email"
                maxLength={254}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
          </div>
          <label>
            <span>{text.reference}</span>
            <input
              required
              maxLength={160}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </label>
          {action === "cancel" ? (
            <>
              <label>
                <span>{text.cancelType}</span>
                <select
                  value={cancellationType}
                  onChange={(event) =>
                    setCancellationType(
                      event.target.value as "ordinary" | "extraordinary",
                    )
                  }
                >
                  <option value="ordinary">{text.ordinary}</option>
                  <option value="extraordinary">{text.extraordinary}</option>
                </select>
              </label>
              <label>
                <span>{text.requestedDate}</span>
                <input
                  maxLength={80}
                  placeholder={text.earliest}
                  value={requestedDate}
                  onChange={(event) => setRequestedDate(event.target.value)}
                />
              </label>
              {cancellationType === "extraordinary" ? (
                <label>
                  <span>{text.reason}</span>
                  <textarea
                    required
                    rows={5}
                    maxLength={2000}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </label>
              ) : null}
            </>
          ) : null}
          <label className="paddle-checkout-consent contract-confirmation">
            <input
              required
              type="checkbox"
              checked={confirmation}
              onChange={(event) => setConfirmation(event.target.checked)}
            />
            <span>
              {action === "cancel"
                ? text.cancelConfirmation
                : text.withdrawConfirmation}
            </span>
          </label>
          {siteKey ? (
            <div ref={turnstile} className="improvement-turnstile" />
          ) : (
            <p className="platform-page-note">{text.captcha}</p>
          )}
          {error ? (
            <p className="contract-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="contract-action-buttons">
            <button
              className="customize-button customize-button--primary"
              type="submit"
              disabled={sending || !token || !confirmation}
            >
              {action === "cancel" ? text.confirmCancel : text.confirmWithdraw}
            </button>
          </div>
        </form>
        <PageActions language={language} navigate={navigate} />
      </article>
    );
  };
}
